/**
 * Meta Pixel Tracking Utility
 *
 * The pixel ID comes from REACT_APP_META_PIXEL_ID (baked into index.html at build
 * time). Builds without it -- dev, UAT -- load no pixel and send nothing.
 *
 * Server-confirmed events (CompleteRegistration, Login, WorkshopRegister,
 * Schedule, SubmitApplication, InitiateCheckout, Purchase) are NOT fired here on
 * our own: the API response carries `meta_events` with the server's
 * deterministic event_id, and fireMetaEvents() fires exactly those, so the
 * browser and server copies always share an ID and Meta deduplicates them.
 *
 * Browser-only: PageView (every route change, see App.js) and ViewContent.
 *
 * Legacy notes for the remaining client-generated events (Lead):
 * 
 * Event Deduplication Strategy: event_id (primary), External ID + FBP (fallback)
 * ------------------------------------------------------------------------------
 * Every tracked event gets an event ID, passed to the pixel as the 4th fbq
 * argument ({ eventID }). Whenever the same action also hits our API, that ID
 * MUST be forwarded to the backend -- via getMetaHeaders(eventId), or in the
 * request body as meta_event_id for the payment endpoints -- so the server CAPI
 * can send it as `event_id` and Meta can pair the two copies.
 *
 * This is what the "event ID dedupe key" coverage metric in Events Manager
 * measures. Firing a browser event without forwarding its ID shows up there as
 * a coverage gap, and the event gets counted twice.
 *
 * external_id + fbp are also sent (see setUserData) and give Meta a secondary
 * matching signal within 48h, but they do NOT count as a dedupe key.
 *
 * Browser pixel gets external_id via fbq('init', PIXEL_ID, { external_id: userId })
 * Server CAPI gets external_id + fbp passed from frontend in API calls.
 */

// Set by index.html only when the pixel actually loads (prod build on gradnext.co).
const META_PIXEL_ID = (() => {
  const id = (typeof window !== 'undefined' && window.__META_PIXEL_ID__) || '';
  return /^\d+$/.test(String(id)) ? String(id) : '';
})();

// Standard events go through fbq('track'); anything else is a custom event.
const STANDARD_EVENTS = new Set([
  'PageView', 'ViewContent', 'Search', 'AddToCart', 'AddToWishlist', 'InitiateCheckout',
  'AddPaymentInfo', 'Purchase', 'Lead', 'CompleteRegistration', 'Contact', 'CustomizeProduct',
  'Donate', 'FindLocation', 'Schedule', 'StartTrial', 'SubmitApplication', 'Subscribe',
]);

/**
 * Generate a unique event ID for deduplication
 * This ID should be passed to both browser pixel and server CAPI
 * @returns {string} Unique event ID
 */
export const generateEventId = () => {
  // Generate a UUID-like string for event deduplication
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  return `evt_${timestamp}_${randomPart}`;
};

// Check if fbq is available
const isFbqAvailable = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null
 */
const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Get the _fbp cookie (Facebook Browser ID)
 * This is set by the Meta Pixel and identifies the browser
 * @returns {string|null}
 */
export const getFbpCookie = () => getCookie('_fbp');

/**
 * Get the _fbc cookie (Facebook Click ID)
 * This is set when user arrives from a Facebook ad click
 * @returns {string|null}
 */
export const getFbcCookie = () => getCookie('_fbc');

/**
 * Get all Meta tracking cookies and identifiers for passing to backend CAPI
 * Call this before API requests that trigger server-side CAPI events
 * @returns {{ fbp: string|null, fbc: string|null }}
 */
export const getMetaCookies = () => ({
  fbp: getFbpCookie(),
  fbc: getFbcCookie(),
});

/**
 * Get Meta tracking data as HTTP headers for passing to backend CAPI
 * Use this with fetch() calls: headers: { ...getMetaHeaders(eventId) }
 * The backend reads these headers to include fbp/fbc and the dedup event_id
 * in server events.
 *
 * Always pass the event ID when the same user action also fires a browser
 * pixel event -- without it the server generates its own ID, which can never
 * match, and Meta counts the event twice.
 *
 * @param {string} [eventId] - Event ID from trackEvent/generateEventId
 * @returns {object} Headers object with X-Meta-Fbp, X-Meta-Fbc, X-Meta-Event-Id (only if values exist)
 */
export const getMetaHeaders = (eventId = null) => {
  const headers = {};
  const fbp = getFbpCookie();
  const fbc = getFbcCookie();
  if (fbp) headers['X-Meta-Fbp'] = fbp;
  if (fbc) headers['X-Meta-Fbc'] = fbc;
  if (eventId) headers['X-Meta-Event-Id'] = eventId;
  return headers;
};

const sha256 = async (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text || typeof crypto === 'undefined' || !crypto.subtle) return '';
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

// Same normalisation as the server (meta_capi._phone): digits, country code first.
const normalisePhone = (phone, countryCode) => {
  let number = String(phone || '').replace(/\D/g, '');
  if (!number) return '';
  const code = String(countryCode || '').replace(/\D/g, '');
  if (code && !number.startsWith(code)) number = code + number;
  else if (!code && number.length === 10) number = `91${number}`;
  return number;
};

let lastMatchedUserId = null;

/**
 * Advanced matching for a signed-in user: fbq('init', id, {em, ph, external_id}),
 * all SHA-256 hashed. external_id is sha256(user id), exactly what the server
 * sends, so browser and server events match the same person.
 *
 * Accepts a user object, or the legacy (userId, userEmail) arguments.
 */
export const setUserData = async (userOrId, userEmail = null) => {
  if (!isFbqAvailable() || !META_PIXEL_ID) return;
  const user = typeof userOrId === 'object' && userOrId !== null
    ? userOrId
    : { id: userOrId, email: userEmail };
  if (!user.id || user.id === lastMatchedUserId) return;
  try {
    const [externalId, em, ph] = await Promise.all([
      sha256(user.id),
      sha256(user.email),
      sha256(normalisePhone(user.phone_number || user.phone, user.phone_country_code)),
    ]);
    const userData = {};
    if (externalId) userData.external_id = externalId;
    if (em) userData.em = em;
    if (ph) userData.ph = ph;
    if (Object.keys(userData).length > 0) {
      window.fbq('init', META_PIXEL_ID, userData);
      lastMatchedUserId = user.id;
    }
  } catch (error) {
    console.error('[Meta Pixel] Error setting user data:', error);
  }
};

/**
 * Clear user data from pixel (call on logout)
 */
export const clearUserData = () => {
  lastMatchedUserId = null;
  if (!isFbqAvailable() || !META_PIXEL_ID) return;
  try {
    // Re-init pixel without user data
    window.fbq('init', META_PIXEL_ID);
  } catch (error) {
    console.error('[Meta Pixel] Error clearing user data:', error);
  }
};

/**
 * Track a Meta Pixel event with optional event ID for deduplication
 * @param {string} eventName - Standard or custom event name
 * @param {object} params - Event parameters
 * @param {string} eventId - Optional event ID for deduplication with CAPI
 */
export const trackEvent = (eventName, params = {}, eventId = null) => {
  if (!isFbqAvailable()) {
    console.warn('Meta Pixel not loaded');
    return null;
  }
  
  try {
    // Generate event ID if not provided
    const dedupEventId = eventId || generateEventId();
    
    // Pass eventID as 4th argument for deduplication
    window.fbq('track', eventName, params, { eventID: dedupEventId });
    
    return dedupEventId;
  } catch (error) {
    console.error('[Meta Pixel] Error tracking event:', error);
    return null;
  }
};

/**
 * Track a custom Meta Pixel event with optional event ID for deduplication
 * @param {string} eventName - Custom event name
 * @param {object} params - Event parameters
 * @param {string} eventId - Optional event ID for deduplication with CAPI
 */
export const trackCustomEvent = (eventName, params = {}, eventId = null) => {
  if (!isFbqAvailable()) {
    console.warn('Meta Pixel not loaded');
    return null;
  }
  
  try {
    // Generate event ID if not provided
    const dedupEventId = eventId || generateEventId();
    
    // Pass eventID as 4th argument for deduplication
    window.fbq('trackCustom', eventName, params, { eventID: dedupEventId });
    
    return dedupEventId;
  } catch (error) {
    console.error('[Meta Pixel] Error tracking custom event:', error);
    return null;
  }
};

/**
 * PageView, browser-only. Called on every route change (App.js), including the
 * first page; the pixel's own history tracking is switched off in index.html so
 * each navigation counts exactly once.
 */
export const trackPageView = () => {
  if (!isFbqAvailable()) return;
  try {
    window.fbq('track', 'PageView');
  } catch (error) {
    console.error('[Meta Pixel] Error tracking PageView:', error);
  }
};

const FIRED_KEY = 'gn_meta_fired';

const alreadyFired = (eventId) => {
  try {
    const fired = JSON.parse(sessionStorage.getItem(FIRED_KEY) || '[]');
    if (fired.includes(eventId)) return true;
    fired.push(eventId);
    sessionStorage.setItem(FIRED_KEY, JSON.stringify(fired.slice(-200)));
  } catch (e) {
    // storage blocked: fall through and fire; Meta still dedups on event_id
  }
  return false;
};

// A checkout that has opened Razorpay but whose Purchase hasn't fired yet.
const PENDING_PURCHASE_KEY = 'gn_pending_purchase';
const PENDING_PURCHASE_TTL = 24 * 60 * 60 * 1000;

/**
 * Call just before Razorpay opens, with the Razorpay order or subscription id.
 * If the buyer leaves before the success handler runs (closed tab, app switch,
 * redirect), recoverPendingPurchase() fires the Purchase on their next visit.
 */
export const rememberPendingPurchase = (ref) => {
  if (!ref) return;
  try {
    localStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify({ ref: String(ref), at: Date.now() }));
  } catch (e) {
    // storage blocked: recovery is best-effort
  }
};

const readPendingPurchase = () => {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_PURCHASE_KEY) || 'null');
    if (pending && pending.ref && Date.now() - pending.at < PENDING_PURCHASE_TTL) return pending;
    localStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch (e) {
    // ignore
  }
  return null;
};

const clearPendingPurchase = (eventId) => {
  const pending = readPendingPurchase();
  if (!pending || (eventId && eventId !== `purchase_${pending.ref}`)) return;
  try {
    localStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch (e) {
    // ignore
  }
};

/**
 * On app load: if a checkout was left pending, ask the server whether that
 * Purchase happened (GET /api/payments/meta-purchase/{ref}, signed-in user's
 * own purchases only) and fire its browser copy with the server's event id.
 * `get` is an axios-style getter resolving to { data }.
 */
export const recoverPendingPurchase = async (get, backendUrl = '') => {
  const pending = readPendingPurchase();
  if (!pending || !isFbqAvailable()) return;
  try {
    const res = await get(`${backendUrl}/api/payments/meta-purchase/${encodeURIComponent(pending.ref)}`);
    const events = (res && res.data && res.data.meta_events) || [];
    fireMetaEvents(events);
  } catch (e) {
    // signed out or offline: try again on the next load, until the TTL passes
  }
};

/**
 * Fire the browser copies of server events returned by the API as
 * `meta_events: [{name, event_id, custom_data}]`.
 */
export const fireMetaEvents = (events) => {
  if (!Array.isArray(events) || !isFbqAvailable()) return;
  events.forEach((event) => {
    if (event && event.name === 'Purchase') clearPendingPurchase(event.event_id);
    if (!event || !event.name || !event.event_id || alreadyFired(event.event_id)) return;
    try {
      const method = STANDARD_EVENTS.has(event.name) ? 'track' : 'trackCustom';
      window.fbq(method, event.name, event.custom_data || {}, { eventID: event.event_id });
    } catch (error) {
      console.error('[Meta Pixel] Error firing server event:', error);
    }
  });
};

const browsingSessionId = () => {
  try {
    let id = sessionStorage.getItem('gn_meta_session');
    if (!id) {
      id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem('gn_meta_session', id);
    }
    return id;
  } catch (e) {
    return 'nosession';
  }
};

/**
 * ViewContent for a page type (pricing, subscription, cohort, coaching, mentor,
 * workshop), once per page type per browsing session: vc_{page}_{session}.
 */
export const trackPageContent = (pageType) => {
  if (!pageType || !isFbqAvailable()) return;
  const eventId = `vc_${pageType}_${browsingSessionId()}`;
  if (alreadyFired(eventId)) return;
  try {
    window.fbq('track', 'ViewContent', { content_name: pageType }, { eventID: eventId });
  } catch (error) {
    console.error('[Meta Pixel] Error tracking ViewContent:', error);
  }
};

/**
 * Track user sign up / registration
 * @param {object} data - { method: 'google'|'email', content_name: 'signup' }
 * @param {string} eventId - Event ID for deduplication with CAPI
 * @returns {string} Event ID used for this event
 */
export const trackSignUp = (data = {}, eventId = null) => {
  return trackEvent('CompleteRegistration', {
    content_name: 'signup',
    status: 'complete',
    ...data
  }, eventId);
};

/**
 * Track user login
 * @param {object} data - { method: 'google'|'email' }
 * @param {string} eventId - Event ID for deduplication with CAPI
 * @returns {string} Event ID used for this event
 */
export const trackLogin = (data = {}, eventId = null) => {
  return trackCustomEvent('Login', {
    content_name: 'login',
    ...data
  }, eventId);
};

/**
 * Track when user initiates checkout (opens payment modal)
 * @param {object} data - { value, currency, content_name, content_ids }
 * @param {string} eventId - Event ID for deduplication with CAPI
 * @returns {string} Event ID used for this event
 */
export const trackInitiateCheckout = (data = {}, eventId = null) => {
  return trackEvent('InitiateCheckout', {
    currency: 'INR',
    ...data
  }, eventId);
};

/**
 * Track successful purchase
 * @param {object} data - { value, currency, content_name, content_ids, content_type }
 * @param {string} eventId - Event ID for deduplication with CAPI
 * @returns {string} Event ID used for this event
 */
export const trackPurchase = (data = {}, eventId = null) => {
  return trackEvent('Purchase', {
    currency: 'INR',
    ...data
  }, eventId);
};

/**
 * Track lead generation (contact form, discovery call booking)
 * @param {object} data - { content_name, content_category }
 * @param {string} eventId - Event ID for deduplication with CAPI
 * @returns {string} Event ID used for this event
 */
export const trackLead = (data = {}, eventId = null) => {
  return trackEvent('Lead', {
    ...data
  }, eventId);
};

/**
 * Track content view (viewing specific course, workshop, etc.)
 * @param {object} data - { content_name, content_type, content_ids }
 * @param {string} eventId - Event ID for deduplication with CAPI
 * @returns {string} Event ID used for this event
 */
export const trackViewContent = (data = {}, eventId = null) => {
  return trackEvent('ViewContent', {
    ...data
  }, eventId);
};

export default {
  fireMetaEvents,
  trackPageContent,
  generateEventId,
  trackEvent,
  trackCustomEvent,
  trackPageView,
  trackSignUp,
  trackLogin,
  trackInitiateCheckout,
  trackPurchase,
  trackLead,
  trackViewContent,
  setUserData,
  clearUserData,
  getMetaCookies,
  getMetaHeaders,
  getFbpCookie,
  getFbcCookie,
};
